import { DB_NAME, STORES } from "./canvas/canvasDbConfig";

export const openCanvasDB = async () => {
  const dbName = DB_NAME;

  // Open without a version to always get a valid connection to the latest DB
  const openLatest = () =>
    new Promise((resolve, reject) => {
      const request = indexedDB.open(dbName);

      request.onsuccess = (event) => resolve(event.target.result);
      request.onerror = (event) => reject(event.target.error);
   
    });

  // Ensure required stores exist; if missing, upgrade by using a safe version >= current
  const ensureStores = async (db) => {
    const missing = (STORES || []).filter(
      (s) => !db.objectStoreNames.contains(s)
    );
    if (missing.length === 0) {
      return db;
    }

    // Helper to open with a specific version and create missing stores
    const upgradeWithVersion = (targetVersion) =>
      new Promise((resolve, reject) => {
        const req = indexedDB.open(dbName, targetVersion);
        req.onupgradeneeded = (event) => {
          const udb = event.target.result;
          (STORES || []).forEach((store) => {
            if (!udb.objectStoreNames.contains(store)) {
              udb.createObjectStore(store, { keyPath: "id" });
            }
          });
        };
        req.onsuccess = (event) => resolve(event.target.result);
        req.onerror = (event) => reject(event.target.error);
        req.onblocked = () =>
          reject(new Error("DB connection blocked during upgrade"));
      });

    // Read the latest observed version from the opened connection and localStorage
    const observedVersion = db.version || 1;
    let targetVersion = observedVersion + 1;
    try {
      const stored = Number(localStorage.getItem("canvasDBVersion") || 0);
      if (stored + 1 > targetVersion) targetVersion = stored + 1;
    } catch {}

    db.close();

    try {
      return await upgradeWithVersion(targetVersion);
    } catch (e) {
      // Handle VersionError by discovering the real current version, then retrying above it
      if (e && e.name === "VersionError") {
        try {
          const latestDb = await openLatest();
          const latestVersion = latestDb.version || observedVersion;
          latestDb.close();
          const retryVersion = Math.max(targetVersion, latestVersion + 1);
          return await upgradeWithVersion(retryVersion);
        } catch (inner) {
          // Final fallback: return the original db so reads can proceed (may still miss stores)
          return await openLatest();
        }
      }
      throw e;
    }
  };

  // Main flow
  try {
    const db = await openLatest();
    try {
      const readyDb = await ensureStores(db);
      // Persist observed version to reduce future mismatches
      try {
        localStorage.setItem("canvasDBVersion", String(readyDb.version));
      } catch {}
      return readyDb;
    } catch (e) {
      // If ensureStores failed due to block, still return the original db so reads can proceed
      return db;
    }
  } catch (e) {
    console.error("❌ Failed to open IndexedDB with fallback logic:", e);
    throw e;
  }
};

export const getCurrentIndexedDBVersion = (dbName) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName);

    request.onsuccess = (event) => {
      const db = event.target.result;
      const version = db.version;
      db.close();
      resolve(version);
    };

    request.onerror = () => {
      reject("Failed to open IndexedDB.");
    };
  });
};

export const deletePagesFromIndexedDB = async (canvasPages) => {
  const dbName = "canvasElementsDB";
  let currentVersion;

  try {
    currentVersion = await getCurrentIndexedDBVersion(dbName);
  } catch (error) {
    console.error("Error getting DB version:", error);
    return;
  }

  const newVersion = currentVersion + 1;

  const request = indexedDB.open(dbName, newVersion);

  request.onupgradeneeded = (event) => {
    const db = event.target.result;
    if (!db.objectStoreNames.contains("canvasElements")) {
      db.createObjectStore("canvasElements", { keyPath: "id" });
    }
  };

  request.onsuccess = (event) => {
    const db = event.target.result;
    const transaction = db.transaction(["canvasElements"], "readwrite");
    const store = transaction.objectStore("canvasElements");

    for (const page of canvasPages) {
      const pageId = page.id || page.pageId;
      if (pageId) store.delete(pageId);
    }

    db.close();
  };

  request.onerror = (event) => {
    console.error("Error opening IndexedDB:", event.target.error);
  };
};

export const saveToIndexedDB = async (storeName, id, data) => {
  try {
    const db = await openCanvasDB(); // Ensure we get a fully ready DB
    // Simple check: if store doesn't exist *after* openCanvasDB, something is fundamentally wrong
    if (!db.objectStoreNames.contains(storeName)) {
      throw new Error(
        `Object store "${storeName}" not found after database initialization.`
      );
    }

    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readwrite");
      const store = tx.objectStore(storeName);
      store.put({ id, data }); // Store data wrapped in an object with 'id' key

      tx.oncomplete = () => {
        resolve(true);
      };
      tx.onerror = () => {
        console.error(
          `IndexedDB: Error saving to store '${storeName}':`,
          tx.error
        );
        reject(tx.error);
      };
    });
  } catch (error) {
    console.error("IndexedDB: Failed to open DB for saving:", error);
    throw error; // Re-throw to propagate the error to the caller
  }
};

export const clearCanvasElementStores = async (
  storeNames = ["canvasElements", "offCanvasElements"],
) => {
  const db = await openCanvasDB();

  const existingStores = storeNames.filter((storeName) =>
    db.objectStoreNames.contains(storeName),
  );

  if (existingStores.length === 0) {
    db.close();
    return;
  }

  await new Promise((resolve, reject) => {
    const tx = db.transaction(existingStores, "readwrite");

    existingStores.forEach((storeName) => {
      tx.objectStore(storeName).clear();
    });

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error("IndexedDB clear aborted"));
  });

  db.close();
};

export const loadFromIndexedDB = async (storeName, id) => {
  try {
    const db = await openCanvasDB(); // Ensure we get a fully ready DB
    // Simple check: if store doesn't exist *after* openCanvasDB, something is fundamentally wrong
    if (!db.objectStoreNames.contains(storeName)) {
      throw new Error(
        `Object store "${storeName}" not found after database initialization.`
      );
    }

    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, "readonly");
      const store = tx.objectStore(storeName);
      const request = store.get(id);
      request.onsuccess = () => {
        // Return the complete object structure, not just the data property
        // This allows the caller to access both id and data properties
        resolve(request.result || null);
      };
      request.onerror = () => {
        console.error(
          `IndexedDB: Error loading from store '${storeName}':`,
          request.error
        );
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("IndexedDB: Failed to open DB for loading:", error);
    throw error; // Re-throw to propagate the error to the caller
  }
};
